import { FC, useEffect, useState } from "react";

import {
  useHookForm,
  useMerchantApiMutation,
  useNotifications,
} from "../../../../hooks";
import {
  IRedirectToInvoice,
  IHookForm,
  IViewVirtualAccountTransferRefund,
  IViewCustomer,
} from "../../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../../utils";

import { View } from "./View";
import { redirectToInvoiceSchema } from "./validationSchema";
import { chargeBearer } from "../../../../enums";

export interface IDestinationTxOrInvoice {
  tx_ref: string;
  amount: number;
  currency: string;
  bearer: chargeBearer;
  status: string;
  created_at: string;
}

export interface IRedirectToInvoiceFormViewProps
  extends IHookForm<IRedirectToInvoice> {
  // invoice props
  customer?: IViewCustomer;
  // destinationTxOrInvoice: IDestinationTxOrInvoice | null;
  selectedTxOrInvoice?: IDestinationTxOrInvoice;
  setSelectedTxOrInvoice: (invoice: IDestinationTxOrInvoice) => void;

  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: string | null;
  refundAmount: number;
  refundCurrency: string;
}

export interface IRedirectToInvoiceFormProps {
  // invoice props
  customer?: IViewCustomer;
  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: string | null;
  // refund details
  refundAmount: number;
  refundCurrency: string;
  // form valuess
  transferRefundId: string;
  sourceTxBearer: chargeBearer;
}

export const RedirectToInvoiceForm: FC<IRedirectToInvoiceFormProps> = (
  props
) => {
  const notificationsHandler = useNotifications();

  const {
    isLoading: isLoadingRedirectToInvoice,
    isSuccess: isSuccessRedirectToInvoice,
    // isError: isErrorRedirectToInvoice,
    data: redirectToInvoiceApiResponse,
    mutate: redirectToInvoicePayment,
  } = useMerchantApiMutation<
    IRedirectToInvoice,
    IViewVirtualAccountTransferRefund
  >(
    "redirectPaymentIssueToInvoice",
    ["getVirtualAccountTransferRefundById"],
    {
      // ignoreActivityBlocker: true,
    },
    (urlTemplate) => urlTemplate.replace("id", props.transferRefundId)
  ).usePostMutation;

  const initialFormValues: IRedirectToInvoice = {
    transfer_refund_id: props.transferRefundId,
    tx_or_invoice_reference: "",
    max_partial_payment_count: 5,
    bearer: props.sourceTxBearer,
    description: "",
  };

  const handleApiSubmit = (data: IRedirectToInvoice) => {
    redirectToInvoicePayment(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset, setValue } = useHookForm({
    initialFormValues,
    validationSchema: redirectToInvoiceSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  errors && console.warn("RedirectToInvoicePaymentForm.errors: ", errors);

  useEffect(() => {
    reset(initialFormValues);
  }, [props.transferRefundId, props.sourceTxBearer]);

  useEffect(() => {
    if (isSuccessRedirectToInvoice) {
      reset();

      // display a notification of success
      notificationsHandler.showNotification({
        ariaLabel: "info notification",
        title: "Payment Issue Settled Successfully",
        kind: "info",
        message:
          "The payment issue has been resolved. It has been accepted as a partial payment.",
        statusIconDescription: "notification",
        timeout: 10000,
      });
    }
  }, [isSuccessRedirectToInvoice]);

  const [selectedTxOrInvoice, setSelectedTxOrInvoice] =
    useState<IDestinationTxOrInvoice>();

  useEffect(() => {
    if (!!selectedTxOrInvoice?.tx_ref)
      setValue("tx_or_invoice_reference", selectedTxOrInvoice.tx_ref);
  }, [selectedTxOrInvoice && selectedTxOrInvoice.tx_ref]);

  return (
    <View
      {...props}
      selectedTxOrInvoice={selectedTxOrInvoice}
      setSelectedTxOrInvoice={setSelectedTxOrInvoice}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
